import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Box, Typography, Alert, Button } from '@mui/material';
import { useAuth } from './AuthProvider';
import logger from '../utils/logger';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { userManager } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    // Use a flag to prevent double processing in React StrictMode
    let processed = false;
    
    const handleCallback = async () => {
      // Guard against double processing
      if (processed) {
        logger.debug('Callback already processed, skipping');
        return;
      }
      processed = true;
      
      // Check if we've already processed this exact callback URL
      const callbackKey = `auth_callback_${window.location.search}`;
      if (sessionStorage.getItem(callbackKey)) {
        logger.debug('This callback URL was already processed');
        navigate('/', { replace: true });
        return;
      }
      
      try {
        setIsProcessing(true);
        setError(null);

        logger.authEvent('callback_processing_started');

        // Mark this callback as processed
        sessionStorage.setItem(callbackKey, 'processed');

        // Process the OIDC callback
        const user = await userManager.signinRedirectCallback();
        
        // Clean up the marker after successful processing
        sessionStorage.removeItem(callbackKey);
        
        if (user) {
          logger.authEvent('callback_successful', {
            userId: user.profile.sub,
            email: user.profile.email,
            sessionState: user.session_state,
          });

          // Get the return URL from state or default to home
          const returnUrl = (user.state as any)?.returnUrl || '/';
          
          logger.navigationEvent('/auth/callback', returnUrl);
          
          // Small delay to ensure state is updated
          setTimeout(() => {
            navigate(returnUrl, { replace: true });
          }, 100);
        } else {
          throw new Error('No user returned from callback');
        }
      } catch (callbackError) {
        const errorMessage = callbackError instanceof Error 
          ? callbackError.message 
          : 'Authentication callback failed';

        logger.error('Auth callback error', {
          error: errorMessage,
          url: window.location.href,
        });

        setError(errorMessage);
        setIsProcessing(false);
      }
    };

    // Only process if we're on the callback URL with parameters
    if (window.location.search || window.location.hash) {
      handleCallback();
    } else {
      // No callback parameters, redirect to login
      logger.authEvent('callback_no_parameters', {
        url: window.location.href,
      });
      navigate('/login', { replace: true });
    }
  }, [userManager, navigate]);

  const handleRetry = () => {
    window.location.href = '/login';
  };

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 3,
          textAlign: 'center',
        }}
      >
        <Alert 
          severity="error" 
          sx={{ mb: 3, maxWidth: 500 }}
        >
          <Typography variant="h6" gutterBottom>
            Authentication Failed
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRetry}
              size="small"
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              onClick={handleGoHome}
              size="small"
            >
              Go Home
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 3,
      }}
    >
      <CircularProgress size={60} sx={{ mb: 3 }} />
      <Typography variant="h5" gutterBottom>
        Completing Sign In
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center">
        Please wait while we complete your authentication...
      </Typography>
      
      {/* Timeout fallback */}
      {isProcessing && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            This is taking longer than expected.
          </Typography>
          <Button
            variant="text"
            onClick={handleRetry}
            sx={{ mt: 1 }}
          >
            Try logging in again
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AuthCallback;